import React from 'react';

interface UseDragAndDropOptions {
  canDrop: boolean;
  onDrop?: (file: File) => void;
}

interface UseDragAndDropReturn {
  isDragOver: boolean;
  handleDragEnter: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
}

/**
 * Shared hook for drag-and-drop file handling.
 * Uses a drag counter ref to correctly track enter/leave across child elements.
 */
export const useDragAndDrop = ({
  canDrop,
  onDrop,
}: UseDragAndDropOptions): UseDragAndDropReturn => {
  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragCounterRef = React.useRef(0);

  // Clear drag-over visual when canDrop becomes false (e.g. attachment limit reached)
  React.useEffect(() => {
    if (!canDrop) {
      setIsDragOver(false);
    }
  }, [canDrop]);

  const handleDragEnter = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;
      if (canDrop && e.dataTransfer.types.includes('Files')) {
        setIsDragOver(true);
      }
    },
    [canDrop],
  );

  const handleDragOver = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (canDrop) {
        e.dataTransfer.dropEffect = 'copy';
      }
    },
    [canDrop],
  );

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (!canDrop) {
        return;
      }
      const files = Array.from(e.dataTransfer.files);
      files.forEach(file => onDrop?.(file));
    },
    [canDrop, onDrop],
  );

  return { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop };
};
